'use client';

import Link from 'next/link';
import { motion } from 'motion/react';
import { ArrowUpRight, Check } from 'lucide-react';
import { ICONS } from '@/lib/iconRegistry';
import { Button } from '@/components/ui/button';
import GlobalTitle from '@shared/GlobalTitle';
import SectionShapes from '@shared/SectionShapes';

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};
const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12 } },
};

export default function VulnerabilityWhySection({ data }) {
    if (!data) return null;

    return (
        <section className="relative py-12 md:py-20 overflow-hidden bg-ghost-white">
            <SectionShapes variant="light" />
            <div className="absolute -top-20 right-0 w-[500px] h-[400px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[400px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

            <div className="relative z-10 max-w-[1440px] mx-auto px-6">
                <motion.div
                    variants={stagger}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-6 lg:gap-16 items-end mb-8 md:mb-10"
                >
                    <motion.div variants={fadeUp}>
                        <GlobalTitle
                            topText={data.topText}
                            titleText={data.titleText}
                            coloredText={data.coloredText}
                            classes="text-left mb-0!"
                        />
                    </motion.div>

                    <motion.div variants={fadeUp} className="lg:border-l-2 lg:border-primary/30 lg:pl-8">
                        {data.paragraphs?.map((para, i) => (
                            <p key={i} className="text-muted-foreground leading-relaxed">
                                {para}
                            </p>
                        ))}
                        {data.buttonUrl && (
                            <Link href={data.buttonUrl} className="inline-block mt-6">
                                <Button size="lg" className="bg-red-gradient border-0 text-sm md:text-base py-5 px-4 btn-glass group cursor-pointer">
                                    <span className="relative z-10 text-sm flex items-center gap-2 text-white group-hover:text-secondary transition-colors duration-400 leading-snug">
                                        {data.buttonText}
                                        <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                                    </span>
                                </Button>
                            </Link>
                        )}
                    </motion.div>
                </motion.div>

                <motion.div
                    variants={stagger}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5"
                >
                    {data.features?.map((feature) => {
                        const Icon = ICONS[feature.icon];
                        return (
                            <motion.div
                                key={feature.label}
                                variants={fadeUp}
                                className="group relative flex flex-col h-full p-5 lg:p-6 rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden hover:border-primary/25 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-1 transition-all duration-300"
                            >
                                <span className="absolute inset-x-0 top-0 h-1 bg-red-gradient scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-500" />

                                <div className="flex items-center gap-3.5 mb-3">
                                    <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors duration-300">
                                        {Icon && <Icon size={20} className="text-primary group-hover:text-white transition-colors duration-300" />}
                                    </div>
                                    <h3 className="min-w-0 font-bold text-base lg:text-[17px] text-gray-900 leading-snug">
                                        {feature.label}
                                    </h3>
                                </div>

                                {feature.labelpara && (
                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                        {feature.labelpara}
                                    </p>
                                )}

                                {feature.labelpoints?.length > 0 && (
                                    <ul className="mt-3 rounded-xl px-3.5 py-1 divide-y divide-gray-200/70 transition-colors duration-300">
                                        {feature.labelpoints.map((point, index) => (
                                            <li key={`${point}-${index}`} className="flex items-start gap-2.5 py-2 text-[13px] leading-snug text-gray-700">
                                                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/10">
                                                    <Check size={10} strokeWidth={3} className="text-primary" />
                                                </span>
                                                <span>{point}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </motion.div>
                        );
                    })}
                </motion.div>
            </div>
        </section>
    );
}
